import React from 'react';
import { Alert, Box, Flex, IconButton, Heading, useSnackbar } from 'pounceV2';

import { extractErrorMessage } from 'Helpers/utils';
import ErrorBoundaryV3 from 'Components/ErrorBoundary/ErrorBoundaryV3';
import { useNotificationPreferences } from './graphql/NotificationPreferences.generated';
import { usePutNotificationPreferences } from './graphql/PutNotificationPreferences.generated';
import { NotificationsSettingsFormV3 } from './NotificationsSettingsFormV3';
import { mergeNotificationPreferences } from './utils';
import { NotificationsDocument } from './graphql/Notifications.generated';

const NotificationsSettingsV3 = ({
  onToggleSettings,
}: {
  onToggleSettings: (flag: boolean) => void;
}) => {
  const { pushSnackbar } = useSnackbar();
  const { data, error, loading } = useNotificationPreferences({
    fetchPolicy: 'cache-and-network',
  });
  const [putNotificationPrefrences] = usePutNotificationPreferences({
    refetchQueries: [
      {
        query: NotificationsDocument,
        variables: { input: { pageSize: 100 } },
      },
    ],
    onError: err => {
      pushSnackbar({
        variant: 'error',
        title: extractErrorMessage(err),
      });
    },
  });

  if (!data && loading) {
    return null;
  }

  const notificationPreferences = mergeNotificationPreferences(
    data?.notificationPreferences ?? null,
  );

  const handleSubmit = async values => {
    await putNotificationPrefrences({
      variables: {
        input: {
          preferences: values.preferences,
        },
      },
    });
  };

  const hasError = Boolean(error);
  return (
    <ErrorBoundaryV3>
      <Box overflowY="auto" mb={2} height="calc(100% - 100px)">
        {hasError && (
          <Box m={4} mb={6}>
            <Alert
              variant="error"
              title="Couldn't load your Notifications"
              description={
                extractErrorMessage(error) ||
                'There was an error when performing your request, please contact support@runpanther.io'
              }
            />
          </Box>
        )}

        <Flex justify="space-between" align="center" p={6}>
          <Heading variant="h5" fontWeight="bold">
            Notifications Settings
          </Heading>

          <Box>
            <IconButton
              variant="ghost"
              icon="close"
              size="medium"
              aria-label="Close Notification Settings"
              onClick={() => onToggleSettings(false)}
            />
          </Box>
        </Flex>

        <Box px={6} py={5}>
          <NotificationsSettingsFormV3
            initialValues={notificationPreferences}
            onSubmit={handleSubmit}
          />
        </Box>
      </Box>
    </ErrorBoundaryV3>
  );
};

export default NotificationsSettingsV3;
